import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { forkJoin } from "rxjs";
import { ClubService } from "../../services/club.service";
import { CourtService } from "../../services/court.service";
import { LucideAngularModule } from "lucide-angular";
import { AuthenticationService } from "../../services/authentication.service";
import { LoginPromptService } from "../../services/login-prompt.service";

type SurfaceFilter = "all" | "indoor" | "outdoor";

interface Court {
  id: number;
  club_id: number;
  name: string;
  img_path: string;
  court_type: 0 | 1 | "Outdoor" | "Indoor";
  match_type_mask: 1 | 2 | 3;
  [key: string]: any;
}

interface Club {
  id: number;
  name: string;
  location: string;
  img_path: string;
  court?: Court[];
  number_of_courts?: number;
  clubOpeningHours?: any[];
  clubPricingHours?: any[];
  price_per_hour?: number;
  imageError?: boolean;
  [key: string]: any;
}

@Component({
  selector: "app-clubs",
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: "./clubs.component.html",
  styleUrl: "./clubs.component.css",
})
export class ClubsComponent implements OnInit {
  clubs: Club[] = [];
  filteredClubs: Club[] = [];
  filterType: SurfaceFilter = "all";
  loading = true;
  selectedClub: Club | null = null;
  isLoggedIn = false;

  constructor(
    private clubService: ClubService,
    private courtService: CourtService,
    private authService: AuthenticationService,
    private loginPromptService: LoginPromptService,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.authService.waitForInitialization();
    this.isLoggedIn = !!this.authService.currentUserValue;
    this.authService.currentUser.subscribe((user) => {
      this.isLoggedIn = !!user;
    });
    this.loadClubs();
  }

  loadClubs() {
    this.loading = true;
    forkJoin({
      clubs: this.clubService.getClubs(),
      courts: this.courtService.getItems(),
    }).subscribe({
      next: ({ clubs, courts }) => {
        this.clubs = clubs as Club[];
        const grouped: { [key: number]: Court[] } = {};
        (courts as Court[]).forEach((c: Court) => {
          grouped[c.club_id] = grouped[c.club_id] || [];
          grouped[c.club_id].push(c);
        });
        this.clubs.forEach((club) => {
          club.court = grouped[club.id] || [];
        });
        this.applyFilter();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  setFilterType(type: SurfaceFilter) {
    this.filterType = type;
    this.applyFilter();
  }

  private applyFilter() {
    this.filteredClubs = this.clubs.filter((club) => {
      if (this.filterType === "all") return true;
      return (club.court ?? []).some((c) =>
        this.matchesSurface(c, this.filterType),
      );
    });
  }

  private matchesSurface(c: Court, sf: SurfaceFilter): boolean {
    if (sf === "all") return true;
    let type = c.court_type;
    if (type === "Indoor") type = 1;
    else if (type === "Outdoor") type = 0;
    return sf === "indoor" ? type === 1 : type === 0;
  }

  selectClub(club: Club) {
    if (this.isLoggedIn) {
      this.router.navigate(["/book-a-court", club.id]);
    } else {
      this.loginPromptService.show(
        "Create an account to book courts and play matches",
        `/book-a-court/${club.id}`,
      );
    }
  }

  getClubWorkingHoursDisplay(club: Club): string {
    const today = new Date();
    const dow = today.getDay();
    const intervals = (club.clubOpeningHours || [])
      .filter((o: any) => o.day_of_week === dow)
      .map((o: any) => ({
        start: this.getHour(o.start_time),
        end: this.getHour(o.end_time),
      }));

    if (!intervals.length) return "Closed";
    return intervals
      .map((i: any) => `${this.formatHour(i.start)}-${this.formatHour(i.end)}`)
      .join(", ");
  }

  getClubMinPrice(club: Club): number {
    const today = new Date();
    const dow = today.getDay();
    const pricing = (club.clubPricingHours || []).filter(
      (p: any) => p.day_of_week === dow,
    );
    if (pricing.length) {
      return Math.min(...pricing.map((p: any) => p.price_rsds));
    }
    return club.price_per_hour || 0;
  }

  private getHour(ts: any): number {
    if (!ts) return 0;
    if (typeof ts === "string") {
      const parts = ts.split(":");
      const h = parseInt(parts[0], 10) || 0;
      const m = parseInt(parts[1], 10) || 0;
      return h + m / 60;
    }
    if (typeof ts === "object" && "hours" in ts) {
      return ts.hours + (ts.minutes || 0) / 60;
    }
    const num = Number(ts);
    return isNaN(num) ? 0 : num;
  }

  private formatHour(h: number): string {
    return Math.floor(h).toString().padStart(2, "0") + ":00";
  }

  handleImageError(event: any, club: Club) {
    club.imageError = true;
  }

  openMaps(location: string, event: Event) {
    event.stopPropagation();
    const url =
      "https://www.google.com/maps/dir/?api=1&destination=" +
      encodeURIComponent(location);
    window.open(url, "_blank");
  }

  goToRankings() {
    this.router.navigate(["/rb-rank"]);
  }

  goToLogin() {
    this.router.navigate(["/login"]);
  }

  goToRegister() {
    this.router.navigate(["/register"]);
  }
}
